import { Provider } from 'react-redux';
import React, { useEffect } from 'react';
import { createRoot } from 'react-dom/client';
import { createBrowserRouter, RouterProvider, useNavigate, createRoutesFromElements, Route } from 'react-router-dom';
import App from './App';
import ErrorBoundary from './components/ErrorBoundary';
import configureStore, { loadState, saveState, filterOfflineChartData } from './configureStore';
import throttle from 'lodash.throttle';
import './styles/reset.css';
import './styles/global.scss';
import 'bootstrap/dist/css/bootstrap.min.css';

// Make sure process is available globally to fix webpack 5 issues
window.process = window.process || {};
window.process.env = window.process.env || {};

// Error handling for initial load
try {
  // Load persisted state and configure store and history
  const persistedState = loadState();
  const { store, history } = configureStore(persistedState);

  // Handle state persistence with error handling
  store.subscribe(
    throttle(() => {
      try {
        const { charts, devices } = store.getState();
        if (Object.keys(charts).length !== 0) {
          saveState({
            charts: filterOfflineChartData(charts),
            devices
          });
        }
      } catch (err) {
        console.error('Error in state persistence:', err);
        // Dispatch error to store if possible
        if (store && typeof store.dispatch === 'function') {
          store.dispatch({
            type: 'ERROR_OCCURRED',
            payload: {
              message: 'Failed to persist state: ' + err.message,
              stack: process.env.NODE_ENV !== 'production' ? err.stack : null,
              actionType: 'STATE_PERSISTENCE_ERROR'
            }
          });
        }
      }
    }, 2000)
  );

  // Create a NavigationSyncComponent to sync history with router
  const NavigationSyncComponent = ({ children }) => {
    const navigate = useNavigate();
    
    useEffect(() => {
      // This function will be called whenever history changes
      const unlisten = history.listen(({ location, action }) => {
        try {
          // Don't navigate if the action came from the router itself
          if (action !== 'POP') {
            navigate(location.pathname + location.search + location.hash, { replace: action === 'REPLACE' });
          }
        } catch (err) {
          console.error('Navigation error:', err);
          // Error will be caught by ErrorBoundary
        }
      });

      // Clean up the listener when component unmounts
      return () => unlisten();
    }, [navigate]);

    return children;
  };

  // Create router with React Router 7 approach
  const router = createBrowserRouter(
    createRoutesFromElements(
      <Route
        path="/*"
        element={
          <Provider store={store}>
            <ErrorBoundary>
              <NavigationSyncComponent>
                <App />
              </NavigationSyncComponent>
            </ErrorBoundary>
          </Provider>
        }
      />
    )
  );

  // React 18 uses createRoot instead of ReactDOM.render
  const container = document.getElementById('root');
  const root = createRoot(container);

  // Use RouterProvider from React Router 7
  root.render(
    <RouterProvider router={router} />
  );

  // For debugging - expose store and redux-first-history in a safe way
  if (process.env.NODE_ENV !== 'production') {
    window.reduxStore = store;
    window.reduxHistory = history;
  }

} catch (err) {
  // Critical error during initialization
  console.error('Critical error during application initialization:', err);
  
  // Render minimal error UI
  const container = document.getElementById('root');
  const root = createRoot(container);
  
  root.render(
    <div className="critical-error-container">
      <div className="critical-error-content">
        <h2>Application Error</h2>
        <p>There was a critical error starting the application.</p>
        {process.env.NODE_ENV !== 'production' && (
          <pre>{err.stack}</pre>
        )}
        <button onClick={() => window.location.reload()} className="btn btn-primary">
          Reload Application
        </button>
      </div>
    </div>
  );
}